import React, { useEffect, useState } from "react";
import { payrollApi } from "../../services/payrollApi";
import { StatusBadge } from "../StatusBadge";
import { useLocalization } from "../../hooks/useLocalization";

const COMPENSATION_LABELS = {
  HOURLY: { en: "Hourly wage", ja: "時給" },
  FIXED_ASSIGNMENT: { en: "Supply pickup assignment pay", ja: "仕入れ・物資引取業務の固定手当" },
};
const UNIT_LABELS = {
  HOURLY: { en: "Per hour", ja: "時間あたり" },
  FIXED_ASSIGNMENT: { en: "Per approved assignment", ja: "承認済み引取1件あたり" },
};

/** Payroll tab - effective-dated wage history for this employee. Fixed-assignment
 * compensation (e.g. the supply-pickup drivers, ¥6,000 per approved pickup) is shown with its
 * own type/unit labels so it is never displayed or mistaken as an hourly rate. */
export const PayrollTab = ({ employeeId }) => {
  const { language } = useLocalization();
  const [profiles, setProfiles] = useState(null);

  useEffect(() => {
    payrollApi.wageHistory(employeeId).then((r) => setProfiles(r.data)).catch(() => setProfiles([]));
  }, [employeeId]);

  if (!profiles) return <p className="text-sm text-slate-500">Loading wage history...</p>;

  return (
    <div className="space-y-4" data-testid="employee-payroll-tab">
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Effective from</th>
              <th className="px-3 py-2 font-medium">Effective to</th>
              <th className="px-3 py-2 font-medium">Compensation type</th>
              <th className="px-3 py-2 font-medium">Amount</th>
              <th className="px-3 py-2 font-medium">Unit</th>
              <th className="px-3 py-2 font-medium">Transport allowance</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => {
              const compType = p.compensation_type === "FIXED_ASSIGNMENT" ? "FIXED_ASSIGNMENT" : "HOURLY";
              const compLabel = COMPENSATION_LABELS[compType][language] || COMPENSATION_LABELS[compType].en;
              const unitLabel = UNIT_LABELS[compType][language] || UNIT_LABELS[compType].en;
              return (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`wage-profile-row-${p.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-700">{p.effective_from}</td>
                  <td className="px-3 py-2 text-slate-500">{p.effective_to || "Current"}</td>
                  <td className="px-3 py-2" data-testid={`wage-profile-comp-type-${p.id}`}>{compLabel}</td>
                  <td className="px-3 py-2" data-testid={`wage-profile-amount-${p.id}`}>¥{Number(p.hourly_rate).toLocaleString()}</td>
                  <td className="px-3 py-2 text-slate-500">{unitLabel}</td>
                  <td className="px-3 py-2">¥{Number(p.transportation_allowance).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={p.status} />
                    {p.compliance_status && p.compliance_status.includes("CONFIRMED") ? (
                      <p className="text-[11px] font-semibold text-emerald-600 mt-0.5" data-testid={`wage-profile-compliance-flag-${p.id}`}>Fixed-assignment classification confirmed</p>
                    ) : p.compliance_status ? (
                      <p className="text-[11px] font-bold text-red-600 mt-0.5" data-testid={`wage-profile-compliance-flag-${p.id}`}>
                        {p.compliance_status === "MINIMUM_WAGE_CONFLICT_REVIEW_REQUIRED" ? "Minimum wage conflict \u2013 owner review required" : p.compliance_status.replaceAll("_", " ")}
                      </p>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {profiles.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500" data-testid="no-wage-profiles-row">No wage profile has been set up yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
